/**
 * CalendarView Component
 * Visualização de calendário com suporte a múltiplas views (Mês/Semana/Dia)
 * Última atualização: Botão "Novo Evento" corrigido para navegar para /events
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarView({ token, user, onLogout }) {
  const { calendarId } = useParams();
  const navigate = useNavigate();
  const [calendar, setCalendar] = useState(null);
  
  usePageTitle(calendar?.name || 'Visualização do Calendário');
  
  const [events, setEvents] = useState([]);
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    calendar_id: calendarId,
    title: '',
    date: '',
    time: '',
    duration: '',
    comment: '',
    status: 'Aguardando',
    color: '#0ea5e9',
    reminder_enabled: false,
    reminder_minutes_before: 60,
    reminder_email: false,
    reminder_whatsapp: false
  });
  
  const STATUS_OPTIONS = ['Aguardando', 'Em Progresso', 'Finalizado'];

  useEffect(() => {
    if (calendarId) {
      fetchCalendar();
      fetchEvents();
      fetchFeriados();
    }
  }, [calendarId, currentDate, viewMode]);

  const fetchCalendar = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/calendars/${calendarId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCalendar(data);
      } else if (response.status === 404) {
        toast.error('Calendário não encontrado ou você não tem permissão para acessá-lo');
        navigate('/calendars');
      } else {
        toast.error('Erro ao carregar calendário');
        navigate('/calendars');
      }
    } catch (error) {
      toast.error('Erro ao carregar calendário');
      navigate('/calendars');
    }
  };

  const fetchEvents = async () => {
    try {
      let start, end;
      
      if (viewMode === 'month') {
        start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      } else if (viewMode === 'week') {
        start = format(startOfWeek(currentDate, { locale: ptBR }), 'yyyy-MM-dd');
        end = format(endOfWeek(currentDate, { locale: ptBR }), 'yyyy-MM-dd');
      } else {
        // Para dia, buscar 7 dias antes e depois
        start = format(addDays(currentDate, -7), 'yyyy-MM-dd');
        end = format(addDays(currentDate, 7), 'yyyy-MM-dd');
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/events?calendar_id=${calendarId}&start_date=${start}&end_date=${end}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('Eventos recebidos:', data);
        setEvents(data);
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeriados = async () => {
    try {
      let start, end;
      
      if (viewMode === 'month') {
        start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      } else if (viewMode === 'week') {
        start = format(startOfWeek(currentDate, { locale: ptBR }), 'yyyy-MM-dd');
        end = format(endOfWeek(currentDate, { locale: ptBR }), 'yyyy-MM-dd');
      } else {
        start = format(addDays(currentDate, -7), 'yyyy-MM-dd');
        end = format(addDays(currentDate, 7), 'yyyy-MM-dd');
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/feriados?start_date=${start}&end_date=${end}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('Feriados recebidos:', data);
        setFeriados(data);
      }
    } catch (error) {
      console.error('Erro ao carregar feriados:', error);
    }
  };

  // Open dialog for new event
  const handleNewEvent = () => {
    setEditingEvent(null);
    setFormData({
      calendar_id: calendarId,
      title: '',
      date: format(currentDate, 'yyyy-MM-dd'),
      time: '',
      duration: '',
      comment: '',
      status: 'Aguardando',
      color: '#0ea5e9',
      reminder_enabled: false,
      reminder_minutes_before: 60,
      reminder_email: false,
      reminder_whatsapp: false
    });
    setDialogOpen(true);
  };
  
  // Open dialog for editing event
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    const dateOnly = event.date.split('T')[0];
    setFormData({
      calendar_id: event.calendar_id,
      title: event.title,
      date: dateOnly,
      time: event.time || '',
      duration: event.duration || '',
      comment: event.comment || '',
      status: event.status,
      color: event.color || '#0ea5e9',
      reminder_enabled: event.reminder_enabled || false,
      reminder_minutes_before: event.reminder_minutes_before || 60,
      reminder_email: event.reminder_email || false,
      reminder_whatsapp: event.reminder_whatsapp || false
    });
    setDialogOpen(true);
  };
  
  // Submit form (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for reminders
    if (formData.reminder_enabled && !formData.reminder_email && !formData.reminder_whatsapp) {
      toast.error('Selecione pelo menos um canal de envio para o lembrete');
      return;
    }
    
    try {
      const url = editingEvent 
        ? `${BACKEND_URL}/api/events/${editingEvent.id}`
        : `${BACKEND_URL}/api/events`;
      
      const method = editingEvent ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingEvent ? 'Evento atualizado!' : 'Evento criado!');
        setDialogOpen(false);
        fetchEvents(); // Reload events
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar evento');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };


  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const filtered = events.filter(e => {
      // Garantir que a data é comparada corretamente (apenas yyyy-MM-dd)
      const eventDate = typeof e.date === 'string' ? e.date.split('T')[0] : e.date;
      return eventDate === dateStr;
    });
    console.log(`Eventos para ${dateStr}:`, filtered);
    return filtered;
  };

  const getFeriadosForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const filtered = feriados.filter(f => {
      const feriadoDate = typeof f.data === 'string' ? f.data.split('T')[0] : f.data;
      return feriadoDate === dateStr;
    });
    return filtered;
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Obter o dia da semana do primeiro dia do mês (0 = Domingo, 6 = Sábado)
    const startDayOfWeek = monthStart.getDay();
    
    // Criar células vazias para os dias antes do dia 1
    const emptyDays = Array(startDayOfWeek).fill(null);

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
          <div key={day} style={{ padding: '0.5rem', fontWeight: 600, textAlign: 'center', color: '#64748b' }}>
            {day}
          </div>
        ))}
        
        {/* Células vazias antes do dia 1 */}
        {emptyDays.map((_, idx) => (
          <div key={`empty-${idx}`} style={{ minHeight: '120px' }}></div>
        ))}
        
        {days.map((day, idx) => {
          const dayEvents = getEventsForDate(day);
          const dayFeriados = getFeriadosForDate(day);
          const isToday = isSameDay(day, new Date());

          return (
            <Card
              key={idx}
              style={{
                minHeight: '120px',
                padding: '0.5rem',
                border: isToday ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                cursor: 'pointer'
              }}
              onClick={() => {
                setCurrentDate(day);
                setViewMode('day');
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                {format(day, 'd')}
              </div>
              
              {dayFeriados.map((feriado, i) => (
                <div
                  key={`f-${i}`}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem',
                    marginBottom: '0.25rem',
                    background: '#fef3c7',
                    borderRadius: '4px',
                    color: '#92400e'
                  }}
                >
                  {feriado.descricao}
                </div>
              ))}
              
              {dayEvents.slice(0, 2).map((event, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem',
                    marginBottom: '0.25rem',
                    background: event.color || '#0ea5e9',
                    color: 'white',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={event.title + (!event.is_creator ? ` (${event.creator_name})` : '')}
                >
                  {event.title}
                  {!event.is_creator && (
                    <span style={{ fontSize: '0.65rem', opacity: 0.8, marginLeft: '0.25rem' }}>
                      • {event.creator_name}
                    </span>
                  )}
                </div>
              ))}
              
              {dayEvents.length > 2 && (
                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                  +{dayEvents.length - 2} mais
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { locale: ptBR });
    const weekEnd = endOfWeek(currentDate, { locale: ptBR });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
          <div key={day} style={{ padding: '0.5rem', fontWeight: 600, textAlign: 'center', color: '#64748b', borderBottom: '2px solid #e2e8f0' }}>
            {day}
          </div>
        ))}
        
        {days.map((day, idx) => {
          const dayEvents = getEventsForDate(day);
          const dayFeriados = getFeriadosForDate(day);
          const isToday = isSameDay(day, new Date());

          return (
            <Card
              key={idx}
              style={{
                minHeight: '400px',
                padding: '0.75rem',
                border: isToday ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                cursor: 'pointer'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '1rem', fontSize: '1.1rem', textAlign: 'center' }}>
                {format(day, 'd')}
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {dayFeriados.map((feriado, i) => (
                  <div
                    key={`f-${i}`}
                    style={{
                      fontSize: '0.8rem',
                      padding: '0.5rem',
                      background: '#fef3c7',
                      borderRadius: '6px',
                      color: '#92400e',
                      borderLeft: '3px solid #fbbf24'
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{feriado.descricao}</div>
                    <div style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>Feriado</div>
                  </div>
                ))}
                
                {dayEvents.map((event, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: '0.8rem',
                      padding: '0.5rem',
                      background: event.color || '#0ea5e9',
                      color: 'white',
                      borderRadius: '6px',
                      borderLeft: `3px solid ${event.color || '#0369a1'}`,
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEvent(event);
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{event.title}</div>
                    {event.time && (
                      <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', opacity: 0.9 }}>
                        {event.time.slice(0, 5)}
                      </div>
                    )}
                    {!event.is_creator && (
                      <div style={{ fontSize: '0.65rem', marginTop: '0.25rem', opacity: 0.85 }}>
                        👤 {event.creator_name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    const dayFeriados = getFeriadosForDate(currentDate);
    const isToday = isSameDay(currentDate, new Date());

    // Criar array de horários (0-23h)
    const hours = Array.from({ length: 24 }, (_, i) => i);

    // Organizar eventos por horário
    const eventsByHour = {};
    dayEvents.forEach(event => {
      if (event.time) {
        const hour = parseInt(event.time.split(':')[0]);
        if (!eventsByHour[hour]) eventsByHour[hour] = [];
        eventsByHour[hour].push(event);
      } else {
        if (!eventsByHour['sem-hora']) eventsByHour['sem-hora'] = [];
        eventsByHour['sem-hora'].push(event);
      }
    });

    return (
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header do dia */}
        <Card style={{ padding: '1.5rem', marginBottom: '1rem', border: isToday ? '2px solid #0ea5e9' : '1px solid #e2e8f0' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
              {format(currentDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </h2>
            {isToday && (
              <p style={{ color: '#0ea5e9', marginTop: '0.5rem', fontWeight: 600 }}>Hoje</p>
            )}
          </div>
        </Card>

        {/* Feriados (se houver) */}
        {dayFeriados.length > 0 && (
          <Card style={{ padding: '1rem', marginBottom: '1rem', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #fbbf24' }}>
            {dayFeriados.map((feriado, i) => (
              <div key={`f-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontSize: '1.5rem' }}>🎉</div>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#92400e' }}>
                    {feriado.descricao}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: '#b45309', marginTop: '0.25rem' }}>
                    {feriado.tipo === 'nacional' ? 'Feriado Nacional' : 
                     feriado.tipo === 'estadual' ? `Feriado Estadual - ${feriado.uf}` : 
                     'Feriado Municipal'}
                  </p>
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Timeline de horários */}
        <Card style={{ padding: '1rem' }}>
          {dayEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <p>Nenhum evento agendado para este dia</p>
              <Button 
                onClick={handleNewEvent} 
                style={{ marginTop: '1rem' }}
              >
                <Plus size={16} style={{ marginRight: '0.5rem' }} />
                Criar Evento
              </Button>
            </div>
          ) : (
            <div>
              {/* Eventos sem horário */}
              {eventsByHour['sem-hora'] && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px dashed #e2e8f0' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#64748b', marginBottom: '0.75rem' }}>
                    ⏰ Sem horário definido
                  </div>
                  {eventsByHour['sem-hora'].map((event, i) => (
                    <div
                      key={`sh-${i}`}
                      style={{
                        padding: '1rem',
                        marginBottom: '0.5rem',
                        background: event.color || '#0ea5e9',
                        color: 'white',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        borderLeft: `4px solid ${event.color || '#0369a1'}`
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent(event);
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '1rem' }}>{event.title}</div>
                      {!event.is_creator && (
                        <div style={{ fontSize: '0.8rem', opacity: 0.85, marginTop: '0.25rem' }}>
                          👤 Criado por: {event.creator_name}
                        </div>
                      )}
                      {event.comment && (
                        <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.5rem' }}>{event.comment}</div>
                      )}
                      <Badge style={{ marginTop: '0.5rem', background: 'rgba(255,255,255,0.2)' }}>
                        {event.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              {/* Timeline por hora */}
              {hours.map(hour => {
                const hourEvents = eventsByHour[hour];
                if (!hourEvents) return null;

                return (
                  <div key={hour} style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem', minHeight: '60px' }}>
                    {/* Coluna de hora */}
                    <div style={{ width: '80px', paddingTop: '0.5rem', textAlign: 'right', fontSize: '0.9rem', fontWeight: 600, color: '#64748b' }}>
                      {String(hour).padStart(2, '0')}:00
                    </div>
                    
                    {/* Coluna de eventos */}
                    <div style={{ flex: 1, borderLeft: '2px solid #e2e8f0', paddingLeft: '1rem' }}>
                      {hourEvents.map((event, i) => (
                        <div
                          key={i}
                          style={{
                            padding: '0.75rem',
                            marginBottom: '0.5rem',
                            background: event.color || '#0ea5e9',
                            color: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            borderLeft: `4px solid ${event.color || '#0369a1'}`
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditEvent(event);
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{event.title}</div>
                              {!event.is_creator && (
                                <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '0.15rem' }}>
                                  👤 {event.creator_name}
                                </div>
                              )}
                              <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '0.25rem' }}>
                                🕐 {event.time.slice(0, 5)}
                                {event.duration && ` (${event.duration} min)`}
                              </div>
                              {event.comment && (
                                <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.5rem' }}>
                                  {event.comment}
                                </div>
                              )}
                            </div>
                            <Badge style={{ background: 'rgba(255,255,255,0.2)', marginLeft: '0.5rem' }}>
                              {event.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    );
  };

  const navigatePrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const getDateRangeLabel = () => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: ptBR });
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate, { locale: ptBR });
      const weekEnd = endOfWeek(currentDate, { locale: ptBR });
      return `${format(weekStart, 'dd MMM', { locale: ptBR })} - ${format(weekEnd, 'dd MMM yyyy', { locale: ptBR })}`;
    } else {
      return format(currentDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    }
  };

  if (loading) return <Layout user={user} onLogout={onLogout}><div>Carregando...</div></Layout>;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>{calendar?.name}</h1>
            <p>{calendar?.description}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button
              variant="outline"
              onClick={() => navigate('/calendars')}
            >
              <ChevronLeft size={18} style={{ marginRight: '0.5rem' }} />
              Voltar
            </Button>
            <Button
              onClick={handleNewEvent}
              data-testid="novo-evento-button"
            >
              <Plus size={18} style={{ marginRight: '0.5rem' }} />
              Novo Evento
            </Button>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            size="sm"
            variant={viewMode === 'month' ? 'default' : 'outline'}
            onClick={() => setViewMode('month')}
          >
            Mês
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'week' ? 'default' : 'outline'}
            onClick={() => setViewMode('week')}
          >
            Semana
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'day' ? 'default' : 'outline'}
            onClick={() => setViewMode('day')}
          >
            Dia
          </Button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Button
            variant="outline"
            size="sm"
            onClick={navigatePrevious}
          >
            <ChevronLeft size={16} />
          </Button>
          <h2 style={{ minWidth: '250px', textAlign: 'center', textTransform: 'capitalize' }}>
            {getDateRangeLabel()}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={navigateNext}
          >
            <ChevronRight size={16} />
          </Button>
          <Button
            size="sm"
            onClick={() => setCurrentDate(new Date())}
          >
            Hoje
          </Button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '16px', height: '16px', background: '#fef3c7', borderRadius: '4px' }}></div>
            Feriados
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '16px', height: '16px', background: '#0ea5e9', borderRadius: '4px' }}></div>
            Eventos
          </div>
        </div>
      </div>

      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}
      
      {/* Dialog for creating/editing event */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }} className="dialog-mobile-responsive">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Editar Evento' : 'Novo Evento'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Label htmlFor="date">Data</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="time">Horário</Label>
                <Input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <Label htmlFor="duration">Duração (minutos)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <Label htmlFor="comment">Comentário</Label>
              <Textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              />
            </div>
            
            {/* Reminder Section */}
            {formData.time && (
              <div style={{ 
                marginBottom: '1rem', 
                padding: '1rem', 
                border: '2px solid #e2e8f0', 
                borderRadius: '8px',
                background: '#f8fafc'
              }}>
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="reminder_enabled"
                    checked={formData.reminder_enabled}
                    onChange={(e) => setFormData({ ...formData, reminder_enabled: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <Label htmlFor="reminder_enabled" style={{ fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }}>
                    🔔 Ativar Lembrete
                  </Label>
                </div>
                
                {formData.reminder_enabled && (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <Label htmlFor="reminder_minutes_before">Avisar quanto tempo antes?</Label>
                      <Select 
                        value={formData.reminder_minutes_before?.toString()} 
                        onValueChange={(value) => setFormData({ ...formData, reminder_minutes_before: parseInt(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutos antes</SelectItem>
                          <SelectItem value="30">30 minutos antes</SelectItem>
                          <SelectItem value="60">1 hora antes</SelectItem>
                          <SelectItem value="120">2 horas antes</SelectItem>
                          <SelectItem value="360">6 horas antes</SelectItem>
                          <SelectItem value="720">12 horas antes</SelectItem>
                          <SelectItem value="1440">1 dia antes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div style={{ marginBottom: '0.5rem' }}>
                      <Label>Enviar lembrete por:</Label>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          id="reminder_email"
                          checked={formData.reminder_email}
                          onChange={(e) => setFormData({ ...formData, reminder_email: e.target.checked })}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <Label htmlFor="reminder_email" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                          📧 Email
                        </Label>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          id="reminder_whatsapp"
                          checked={formData.reminder_whatsapp}
                          onChange={(e) => setFormData({ ...formData, reminder_whatsapp: e.target.checked })}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <Label htmlFor="reminder_whatsapp" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                          💬 WhatsApp
                        </Label>
                      </div>
                    </div>
                    
                    {!formData.reminder_email && !formData.reminder_whatsapp && (
                      <p style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '0.5rem' }}>
                        ⚠️ Selecione pelo menos um canal de envio
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            
            {!formData.time && (
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem', fontStyle: 'italic' }}>
                💡 Preencha o horário para ativar lembretes
              </p>
            )}
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <Button type="submit" style={{ flex: 1 }}>
                {editingEvent ? 'Atualizar' : 'Criar'} Evento
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
